import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GET_ORGANIZATION_STATES } from 'src/graphql/voting_areas/queries';

const EMPTY = [];

/**
 * The states an organization operates in, as two-letter codes.
 *
 * Derived from the org's voting areas because that is the only place the product records it --
 * there is no state column on `organizations`. Every organization that owns a question bank has
 * at least one, so a caller that gets none is looking at an org nothing can be scoped to yet,
 * which is a case to disable rather than to paper over with a default.
 */
export const useOrganizationStates = (organizationId, { skip = false } = {}) => {
  // Coerced because the id reaches callers from several places -- a route param and
  // OrganizationContext both hand it over as a string, and the query declares `Int!`.
  const orgId = Number(organizationId);

  const { data, loading } = useQuery(GET_ORGANIZATION_STATES, {
    variables: { orgId },
    skip: skip || !Number.isFinite(orgId) || orgId <= 0,
  });

  const states = useMemo(() => {
    const rows = data?.rows || [];
    if (!rows.length) return EMPTY;

    return [
      ...new Set(
        rows
          .map((row) => row.organization_voting_areas_voting_area?.voting_areas_state?.short_name)
          .filter(Boolean)
      ),
    ].sort();
  }, [data]);

  return { states, loading };
};
